/* eslint-disable max-len */
import {
  Show,
  SimpleShowLayout,
  TextField,
  ImageField,
  useTranslate,
  FunctionField,
  useShowContext,
  Labeled
} from "react-admin";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import {
  AppointmentDetailsIcon,
  EmptyAppointmentIcon,
  StaffDetailsIcon
} from "assets/Icons";
import { Avatar } from "@mui/material";
import CustomList from "./CustomList";
import CustomAppointmentListContent from "./CustomAppointmentListContent";
import * as User from "providers/user";
import { useParams } from "react-router-dom";

const ConditionalTextField = ({ sourceName }: { sourceName: string }) => {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (sourceName === "cover_photo_url" && record?.cover_photo_url) {
    return (
      <Labeled label={translate("artists.cover_photo")}>
        <ImageField source="cover_photo_url" />
      </Labeled>
    );
  }

  if (record?.[sourceName]) {
    return (
      <Labeled label={translate(`artists.${sourceName}`)}>
        <TextField source={sourceName} />
      </Labeled>
    );
  }

  return null;
};

export default function ArtistShow() {
  const translate = useTranslate();
  const { user, permissions } = User.useUser();
  const { id } = useParams<{ id: string }>();

  const renderInstagram = (record: any) => {
    if (record.instagram_id) {
      return <>Authorized</>;
    }
    return <>Unauthorized</>;
  };

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((location: any) => location.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((location: any) => location.id);

  const filter = {
    cancelled_at: {
      equals: null
    },
    appointment_services: {
      some: {
        artist_id: id
      }
    },
    location_id: { in: locationIds },
    is_blocked_time: {
      equals: false
    }
  };

  return (
    <Show
      actions={<CustomHeaderActions title={translate("pagenames.artist")} />}
    >
      <div className="w-full">
        <div className="h-min w bg-mono-primaryLightBgColor rounded-[8px] mb-4">
          <SimpleShowLayout>
            <ShowAndEditDetails
              title={translate("artists.artist_details")}
              view="show"
              icon={<StaffDetailsIcon />}
            />
            <TextField source="is_active" label={translate("artists.active")} />
            <FunctionField
              source="instagram_id"
              label="Instagram"
              render={renderInstagram}
            />
            <FunctionField
              label={translate("common.user")}
              render={(record: any) => (
                <div className="line-clamp-2 break-all">
                  {`${record.user.first_name} ${record.user.last_name}`}
                </div>
              )}
            />
            <ConditionalTextField sourceName="bio" />
            <FunctionField
              label={translate("artists.artist_profile_photo")}
              render={(record: any) => (
                <>
                  {record?.cover_photo_url ? (
                    <Avatar
                      sx={{
                        bgcolor: "rgba(238, 234, 228, 0.9)",
                        color: "rgba(48, 47, 46, 0.95)",
                        width: "32px",
                        height: "32px",
                        fontFamily: "Inter",
                        fontSize: "18px"
                      }}
                      alt={record?.user?.first_name}
                      src={record?.cover_photo_url || "monogram"}
                    />
                  ) : (
                    <div>-</div>
                  )}
                </>
              )}
            />
          </SimpleShowLayout>
        </div>
        <div className="h-min w-full bg-mono-primaryLightBgColor rounded-[8px] py-2">
          <div className="px-4">
            <ShowAndEditDetails
              title={translate("users.appointments")}
              view="show"
              icon={<AppointmentDetailsIcon />}
              hasEdit={false}
              marginBottom="0px"
              borderBottom="none"
            />
          </div>
          <CustomList
            resource="Appointment"
            storeKey="ArtistAppointment.listParams"
            filter={filter}
            title={translate("pagenames.appointment")}
            hasCreate={false}
            emptyIcon={<EmptyAppointmentIcon />}
            // empty={false}
            sort={{ field: "start_time", order: "DESC" }}
            actions={<div />}
          >
            <CustomAppointmentListContent fromArtistDetails />
          </CustomList>
        </div>
      </div>
    </Show>
  );
}
